import { ANALYTICS_EVENTS, ANALYTICS_PROPERTY_KEYS, COMPONENT_OUTCOMES, DCM_PAGES } from '../constants';
import { trackAnalyticsEvent } from '../core/analyticsClient';

type LineItemFormFieldContext = {
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
};

const buildLineItemFormFieldProps = ({
  campaignId,
  lineItemId,
  adAccountId,
}: LineItemFormFieldContext): Record<string, unknown> => ({
  [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
  [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
  [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
  [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_BUILDER,
  [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
});

export type TrackLineItemStartDateEnteredParams = LineItemFormFieldContext & {
  startDate: string;
};

export const trackLineItemStartDateEntered = ({
  campaignId,
  lineItemId,
  adAccountId,
  startDate,
}: TrackLineItemStartDateEnteredParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.LINE_ITEM_START_DATE_ENTERED, {
    ...buildLineItemFormFieldProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.START_DATE]: startDate,
  });
};

export type TrackLineItemEndDateEnteredParams = LineItemFormFieldContext & {
  endDate: string;
};

export const trackLineItemEndDateEntered = ({
  campaignId,
  lineItemId,
  adAccountId,
  endDate,
}: TrackLineItemEndDateEnteredParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.LINE_ITEM_END_DATE_ENTERED, {
    ...buildLineItemFormFieldProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.END_DATE]: endDate,
  });
};

export type TrackGenderSelectedParams = LineItemFormFieldContext & {
  genders: string[];
};

export const trackGenderSelected = ({
  campaignId,
  lineItemId,
  adAccountId,
  genders,
}: TrackGenderSelectedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.GENDER_SELECTED, {
    ...buildLineItemFormFieldProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.GENDERS]: genders,
    [ANALYTICS_PROPERTY_KEYS.GENDER_COUNT]: genders.length,
  });
};

export type TrackAgeGroupsSelectedParams = LineItemFormFieldContext & {
  ageGroups: string[];
};

export const trackAgeGroupsSelected = ({
  campaignId,
  lineItemId,
  adAccountId,
  ageGroups,
}: TrackAgeGroupsSelectedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.AGE_GROUPS_SELECTED, {
    ...buildLineItemFormFieldProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.AGE_GROUPS]: ageGroups,
    [ANALYTICS_PROPERTY_KEYS.AGE_GROUP_COUNT]: ageGroups.length,
  });
};

export type TrackDeviceTargetingSelectedParams = LineItemFormFieldContext & {
  platforms: string[];
};

export const trackDeviceTargetingSelected = ({
  campaignId,
  lineItemId,
  adAccountId,
  platforms,
}: TrackDeviceTargetingSelectedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.DEVICE_TARGETING_SELECTED, {
    ...buildLineItemFormFieldProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.PLATFORMS]: platforms,
    [ANALYTICS_PROPERTY_KEYS.PLATFORM_COUNT]: platforms.length,
  });
};

export type TrackAdLengthSelectedParams = LineItemFormFieldContext & {
  adDuration: string;
};

export const trackAdLengthSelected = ({
  campaignId,
  lineItemId,
  adAccountId,
  adDuration,
}: TrackAdLengthSelectedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.AD_LENGTH_SELECTED, {
    ...buildLineItemFormFieldProps({ campaignId, lineItemId, adAccountId }),
    [ANALYTICS_PROPERTY_KEYS.AD_DURATION]: adDuration,
  });
};
